import { minesweeper, type MinesweeperView } from '@cardengine/shared';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useStore } from '../store.js';

const { FLAGGED, MINE, FLAGGED_MINE, WRONG_FLAG, DETONATED } = minesweeper;

/**
 * The face, which is also the restart button.
 *
 * Kept because it is genuinely the best control on the original: it is the only
 * thing on screen that is both the state of the game and the way to start
 * another, and after one board nobody has to be told what it does. The other
 * solo games ask before abandoning a deal in progress; this one does not,
 * because pressing the face *is* the gesture for "again", and a confirmation
 * dialog in front of it would be answering a question nobody asked.
 */
function Face({ phase, poised, onClick }: { phase: string; poised: boolean; onClick: () => void }) {
  const face = phase === 'won' ? '😎' : phase === 'lost' ? '😵' : poised ? '😮' : '🙂';
  const label =
    phase === 'won'
      ? 'Board cleared. Start another'
      : phase === 'lost'
        ? 'Blown up. Try again'
        : 'Start again';

  return (
    <button type="button" className="msface" onClick={onClick} title={label} aria-label={label}>
      <span aria-hidden="true">{face}</span>
    </button>
  );
}

/**
 * The seven-segment readouts either side of the face.
 *
 * Three digits and a leading zero pad, as on the original — and the pad is not
 * only nostalgia: a counter that changes width as it counts down would shift
 * the board sideways under the cursor.
 */
function Readout({ value, label }: { value: number; label: string }) {
  const clamped = Math.max(-99, Math.min(999, value));
  const text =
    clamped < 0 ? `-${String(Math.abs(clamped)).padStart(2, '0')}` : String(clamped).padStart(3, '0');

  return (
    <span className="msreadout" role="status" aria-label={`${label}: ${value}`}>
      {text}
    </span>
  );
}

/** Counts up from the moment the board was dealt, and stops when it ends. */
function Clock({ startedAt, running }: { startedAt: number | null; running: boolean }) {
  const [seconds, setSeconds] = useState(() => elapsed(startedAt));
  // Frozen at whatever it read when the board ended, rather than reset — how
  // long it took is part of the result.
  const stopped = useRef(seconds);

  useEffect(() => {
    if (!running) return;
    setSeconds(elapsed(startedAt));
    const timer = setInterval(() => {
      const now = elapsed(startedAt);
      stopped.current = now;
      setSeconds(now);
    }, 1_000);
    return () => clearInterval(timer);
  }, [running, startedAt]);

  return <Readout value={running ? seconds : stopped.current} label="Time" />;
}

function elapsed(startedAt: number | null): number {
  if (startedAt === null) return 0;
  return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
}

/** "Row 2, column 3" — how a square gets announced. */
function squareName(cell: number, width: number): string {
  return `Row ${Math.floor(cell / width) + 1}, column ${(cell % width) + 1}`;
}

/** What a square is, in words, for anyone not looking at it. */
function squareLabel(square: number): string {
  if (square >= 1) return `${square}`;
  switch (square) {
    case 0:
      return 'blank';
    case FLAGGED:
      return 'flagged';
    case FLAGGED_MINE:
      return 'mine, flagged';
    case WRONG_FLAG:
      return 'flagged, no mine';
    case MINE:
      return 'mine';
    case DETONATED:
      return 'the mine you opened';
    default:
      return 'closed';
  }
}

interface MinesweeperBoardProps {
  view: MinesweeperView;
  /** How the board ended, for the status strip. Null while it is still live. */
  outcome: string | null;
}

export function MinesweeperBoard({ view, outcome }: MinesweeperBoardProps) {
  const reveal = useStore((s) => s.minesweeperReveal);
  const flag = useStore((s) => s.minesweeperFlag);
  const chord = useStore((s) => s.minesweeperChord);
  const playAgain = useStore((s) => s.playAgain);
  const rematch = useStore((s) => s.rematch);
  const flagMode = useStore((s) => s.flagMode);
  const setFlagMode = useStore((s) => s.setFlagMode);
  const rejection = useStore((s) => s.rejection);

  // Only for the face: the raised eyebrows while a square is held down. Purely
  // decorative, and deliberately not part of any move.
  const [poised, setPoised] = useState(false);
  const live = view.phase === 'playing';

  useEffect(() => {
    if (!live) setPoised(false);
  }, [live]);

  function press(cell: number, square: number): void {
    if (!live) return;
    // An open number takes the chord; everything else takes whichever of open
    // or flag the mode says. One press, three meanings, decided by what is
    // under it — which is how the original behaves without ever explaining it.
    if (square > 0) chord(cell);
    else if (flagMode) flag(cell);
    else reveal(cell);
  }

  function secondary(event: MouseEvent, cell: number, square: number): void {
    event.preventDefault();
    if (!live) return;
    if (square > 0) chord(cell);
    else flag(cell);
  }

  return (
    <div className="msboard-wrap">
      {/* One cabinet, marquee to coin plate.

          Readouts, grid and switch are rows of the same panel, and only the
          grid has a width of its own — everything else stretches to whatever
          the board came out at, so the strip lines up with the squares on a
          5×5 and on a 30×16 without either being told the other's size. The
          marquee and the plate take their width from the same place, which is
          why a cabinet round an expert board still looks like one object. */}
      <div className="mscab">
        {/* The lit sign on top. Painted signage rather than the machine's own
            LED type, because on a real cabinet the marquee is the one part
            that is artwork — so it gets the site's display face, and this
            reads as a Freehouse machine rather than a licensed one. */}
        <div className="mscab__marquee">
          <span className="mscab__marquee-name">Minesweeper</span>
        </div>

        {/* The black surround the screen is set into. Its own element rather
            than more border on the casing, because the glow belongs to the
            screen and the moulding does not. */}
        <div className="mscab__bezel">
          <div className="mscab__panel">
            <div className="mshead">
              <Readout value={view.flagsLeft} label="Mines left" />
              <Face phase={view.phase} poised={poised} onClick={playAgain} />
              <Clock startedAt={view.startedAt} running={live} />
            </div>

            <div
              className="msboard"
              style={
                {
                  '--ms-cols': view.width,
                  '--ms-rows': view.height,
                } as React.CSSProperties
              }
              role="group"
              aria-label={`${view.width} by ${view.height} grid, ${view.mineCount} mines`}
              // Belt and braces on the context menu: the squares each stop it, and
              // so does the grid's own padding, so a right-click aimed slightly
              // wide never puts a browser menu over the board mid-game.
              onContextMenu={(event) => event.preventDefault()}
            >
              {view.cells.map((square, cell) => {
                const open = square >= 0;
                const ended =
                  square === DETONATED || square === MINE || square === WRONG_FLAG || square === FLAGGED_MINE;

                return (
                  <button
                    key={cell}
                    type="button"
                    className={[
                      'mscell',
                      open ? 'mscell--open' : '',
                      square > 0 ? `mscell--n${square}` : '',
                      square === FLAGGED ? 'mscell--flag' : '',
                      ended ? 'mscell--end' : '',
                      square === DETONATED ? 'mscell--boom' : '',
                      square === WRONG_FLAG ? 'mscell--wrong' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={!live && !ended}
                    onClick={() => press(cell, square)}
                    onContextMenu={(event) => secondary(event, cell, square)}
                    onPointerDown={() => live && setPoised(true)}
                    onPointerUp={() => setPoised(false)}
                    onPointerLeave={() => setPoised(false)}
                    aria-label={`${squareName(cell, view.width)}, ${squareLabel(square)}`}
                  >
                    <span aria-hidden="true">{glyph(square)}</span>
                  </button>
                );
              })}
            </div>

            {/* One row, two jobs, so the panel is exactly as tall at the end of a
                board as it was during it. A finished game swaps the switch for
                the way on, rather than adding a row and shifting a centred
                cabinet — which would move the board at the one moment the player
                is trying to read it. */}
            <div className="msfoot">
              {live ? (
                /* A right-click flags, which is no use on a phone and awkward
                   from a keyboard. This is the way in for both, and it latches
                   rather than being held, so it survives a one-handed game.

                   A switch rather than a checkbox: a native box in the site's
                   font was the last thing on the panel still reading as a web
                   page, and the lamp answers "am I about to flag or open" from
                   the corner of an eye — which is the one bit of state that
                   costs you the board if you misread it. */
                <button
                  type="button"
                  role="switch"
                  aria-checked={flagMode}
                  className="msswitch"
                  onClick={() => setFlagMode(!flagMode)}
                >
                  <span className="msswitch__lamp" aria-hidden="true" />
                  <span>Flag mode</span>
                  <span className="msswitch__hint" aria-hidden="true">
                    or right-click
                  </span>
                </button>
              ) : (
                <div className="msactions">
                  <button type="button" className="msbtn" onClick={rematch}>
                    Change board
                  </button>
                  <button type="button" className="msbtn msbtn--go" onClick={playAgain} autoFocus>
                    New board
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* No coins — which, on a site whose whole pitch is no accounts and no
            gatekeeping, is the one piece of cabinet furniture that is actually
            saying something. */}
        <div className="mscab__plate">
          <span className="mscab__slot" aria-hidden="true" />
          <span className="mscab__plate-text">Free play</span>
          <span className="mscab__slot" aria-hidden="true" />
        </div>
      </div>

      {/* The machine's one line of prose, and the only place the ending is
          written out. Under the cabinet rather than inside it: these sentences
          are longer than a small board is wide, and a machine that changes size
          when it tells you something is not a machine.

          A refusal outranks the ending, because a refusal is about the thing
          you just tried and the ending will still be there afterwards. The row
          is reserved either way, so nothing on screen moves when it fills. */}
      <p
        className={[
          'msstatus',
          rejection ? 'msstatus--bad is-shown' : '',
          !rejection && outcome ? `msstatus--${view.phase} is-shown` : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="status"
      >
        {rejection?.text ?? outcome ?? ' '}
      </p>
    </div>
  );
}

function glyph(square: number): string {
  if (square > 0) return String(square);
  switch (square) {
    case FLAGGED:
      return '🚩';
    case WRONG_FLAG:
      return '❌';
    case FLAGGED_MINE:
      return '🚩';
    case MINE:
    case DETONATED:
      return '💣';
    default:
      // Both a blank open square and a closed one. They are told apart by the
      // pressed-in look, not by anything written in them.
      return '';
  }
}
